import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import classes from "./BookingPhone.module.css"

import DateChooser from "./DateChooser"
import BookingOverview from "./BookingOverview"
import Equipment from "../Equipment"
import BuchungsDetails from "./BuchungsDetails"
import AddPackage from "../AddPackage"
import Checkout from "./Checkout"
import BookingConfirmation from "./BookingConfirmation"
import PaketAndGroupSize from "./PaketAndGroupSize"
import TimePickerPacket from "./TimePickerPacket"
import TimePicker from "./TimePicker"
import { useBooking } from "../../../store/bookingStore";
import { useBookingPricingSync } from "../../../hooks/useBookingPricingSync";
import { normalizePaidEquipmentInput } from "../../../pages/StudioProfile/paidEquipmentHelpers";
import { trackBookingStepViewed } from "../../../util/tracking";

export const BookingPhone = ({ onClose, bookingStudioData , data, studioCreativesData}) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const contentRef = useRef(null);
    const { bookingData, updateBookingData } = useBooking();

    const hasPaidEquipmentStep = useMemo(() => {
        const api = normalizePaidEquipmentInput(
            data?.optionalBookableEquipment ?? bookingStudioData?.optionalBookableEquipment
        );
        return api.length > 0;
    }, [data?.optionalBookableEquipment, bookingStudioData?.optionalBookableEquipment]);

    // Lock body scroll when modal is open
    useEffect(() => {
        // Store original styles
        const originalStyle = window.getComputedStyle(document.body).overflow;
        const originalPosition = window.getComputedStyle(document.body).position;
        const originalTop = window.getComputedStyle(document.body).top;
        
        // Get current scroll position
        const scrollY = window.scrollY;
        
        // Apply styles to prevent scrolling
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
        
        // iOS Safari specific fixes
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
            document.body.style.height = '100vh';
            document.body.style.height = '100dvh'; // Use dynamic viewport height if supported
        }
        
        // Cleanup function to restore original styles
        return () => {
            document.body.style.overflow = originalStyle;
            document.body.style.position = originalPosition;
            document.body.style.top = originalTop;
            document.body.style.width = '';
            document.body.style.height = '';
            
            // Restore scroll position
            window.scrollTo(0, scrollY);
        };
    }, []);

    // Browser back / URL sync: Studio.jsx useModalSearchParamHistory for ?booking-phone=open

    // Check if there are any creative packages available
    const hasCreativePackages = useMemo(() => {

        const packagesFromCreatives = studioCreativesData?.some(
            (sc) => sc?.creative?.packages?.items && Array.isArray(sc.creative.packages.items) && sc.creative.packages.items.length > 0
        );
        
        // Check both sources
        const hasStudioDataPackages = bookingStudioData?.creativePackages && Array.isArray(bookingStudioData.creativePackages) && bookingStudioData.creativePackages.length > 0;
        const hasCreativesPackages = packagesFromCreatives;
        
        return hasStudioDataPackages || hasCreativesPackages;
    }, [bookingStudioData?.creativePackages, studioCreativesData]);

    // Check if studio has package-based pricing
    // IMPORTANT:
    // - Only treat the studio as "package pricing" when pricingMode is explicitly "PACKAGES"
    // - Studios with pricingMode === "HOURLY" must stay hourly even if packages exist
    const isPackagePricing = useMemo(() => {
        const pricingMode = data?.pricingMode || bookingStudioData?.pricingMode;
        return pricingMode === 'PACKAGES';
    }, [data?.pricingMode, bookingStudioData?.pricingMode]);

    useBookingPricingSync({
        bookingStudioData,
        bookingData,
        updateBookingData,
        studioCreativesData,
        isPackagePricing,
    });

    // Function to get title based on slide index
    const getTitle = (slideIndex) => {
        const titles = [];
        
        // Slide 0: Package selection (if package pricing)
        if (isPackagePricing) {
            titles.push("Paket & Gruppengröße");
        }
        
        // Date selection
        titles.push("Datum auswählen");

        // Time selection (always directly after date)
        titles.push("Verfügbare Uhrzeiten wählen");
        
        // Booking overview
        titles.push("Buchung starten");

        // Creative packages (if available)
        if (hasCreativePackages) {
            titles.push("Kreative engagieren");
        }

        if (hasPaidEquipmentStep) {
            titles.push("Zusätzliches Equipment");
        }
        
        // Booking details
        titles.push("Buchungsdetails");
        
        // Checkout
        titles.push("Checkout");
        
        // Confirmation
        titles.push("Buchung bestätigen");
        
        return titles[slideIndex] || "";
    };
    
    const nextSlide = useCallback(() => {
        setCurrentSlide(prev => {
            // pkg + date + time + overview + creative(0/1) + equipment(0/1) + details + checkout + confirm
            const total =
                (isPackagePricing ? 1 : 0) +
                1 +
                1 +
                1 +
                (hasCreativePackages ? 1 : 0) +
                (hasPaidEquipmentStep ? 1 : 0) +
                1 +
                1 +
                1;
            
            if (prev < total - 1) {
                setIsTransitioning(true);
                setTimeout(() => {
                    setIsTransitioning(false);
                }, 300);
                return prev + 1;
            }
            return prev;
        });
    }, [isPackagePricing, hasCreativePackages, hasPaidEquipmentStep]);

    const prevSlide = () => {
        if (currentSlide > 0) {
            setIsTransitioning(true);
            setTimeout(() => {
                setCurrentSlide(currentSlide - 1);
                setIsTransitioning(false);
            }, 300);
        } else if (currentSlide === 0) {
            // Clear date ONLY if user is in package pricing mode
            if (isPackagePricing) {
                updateBookingData('date', '');
            }
            // Call onClose to close the modal
            onClose();
        }
    };

    const slides = useMemo(() => {
        const slideArray = [];
        
        // 1: Package selection (if package pricing)
        if (isPackagePricing) {
            slideArray.push(
                <PaketAndGroupSize
                    key="package"
                    bookingStudioData={bookingStudioData}
                    data={data}
                    nextSlide={nextSlide}
                />
            );
        }
        
        // 2: Date selection
        slideArray.push(
            <DateChooser
                key="date"
                bookingStudioData={bookingStudioData}
                nextSlide={nextSlide}
            />
        );

        // 3: Time selection directly after date
        if (isPackagePricing) {
            // Package-based time selection
            slideArray.push(
                <TimePickerPacket
                    key="timePacket"
                    bookingStudioData={bookingStudioData}
                    nextSlide={nextSlide}
                />
            );
        } else {
            // Hourly-based time selection
            slideArray.push(
                <TimePicker
                    key="time"
                    bookingStudioData={bookingStudioData}
                    nextSlide={nextSlide}
                />
            );
        }
        
        // 4: Booking overview
        slideArray.push(
            <BookingOverview
                key="overview"
                setCurrentSlide={setCurrentSlide}
                bookingStudioData={bookingStudioData}
                nextSlide={nextSlide}
            />
        );
        
        // 5: Creative packages (if available)
        if (hasCreativePackages) {
            slideArray.push(
                <AddPackage 
                    key="addPackage"
                    processedProfilePicture={bookingStudioData?.profilePicture} 
                    firstName={bookingStudioData?.firstName} 
                    userId={bookingStudioData?.userId} 
                    studioId={bookingStudioData?.id} 
                    studioCreativesData={studioCreativesData}
                    nextSlide={nextSlide}
                />
            );
        }

        // 6: Equipment (only when API has optional bookable items or fake list is non-empty)
        if (hasPaidEquipmentStep) {
            slideArray.push(
                <Equipment
                    key="equipment"
                    paidEquipment={data?.optionalBookableEquipment ?? bookingStudioData?.optionalBookableEquipment}
                    nextSlide={nextSlide}
                />
            );
        }
        
        // 7: Details, checkout, confirmation
        slideArray.push(
            <BuchungsDetails key="details" nextSlide={nextSlide}/>,
            <Checkout
                key="checkout"
                studioCreativesData={studioCreativesData}
                setCurrentSlide={setCurrentSlide}
                data={data}
                nextSlide={nextSlide}
            />,
            <BookingConfirmation key="confirmation" data={data} onClose={onClose}/>
        );
        
        return slideArray;
    }, [
        isPackagePricing,
        hasCreativePackages,
        hasPaidEquipmentStep,
        bookingStudioData,
        data,
        studioCreativesData,
        nextSlide,
        setCurrentSlide,
        onClose,
    ]);
    
    const totalSlides = slides.length;

    const progressPercentage = ((currentSlide + 1) / totalSlides) * 100;
    const currentStepTitle = getTitle(currentSlide);

    useEffect(() => {
        trackBookingStepViewed({
            studio: data,
            studioId: data?.id || bookingStudioData?.id,
            stepIndex: currentSlide + 1,
            stepName: currentStepTitle,
            source: "booking_phone",
        });
    }, [bookingStudioData?.id, currentSlide, currentStepTitle, data]);

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [currentSlide]);

  return (
    <div className={classes.container}>
        <div className={classes.mainContainer}>
            <header className={classes.header}>
            <div className={classes.headerContainer}>
                    <div 
                        className={classes.closeButton} 
                        onClick={currentSlide === 0 ? onClose : prevSlide}
                    >
                        {currentSlide !== totalSlides - 1 && (
                            <svg xmlns="http://www.w3.org/2000/svg" width="9" height="17" viewBox="0 0 9 17" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M0 7.91991L7.90197 0L8.95756 1.05799L2.07028 7.96091L9 15.4851L7.90306 16.5L0.0119278 7.93186L0 7.91991Z" fill="black"/>
                            </svg>
                        )}
                    </div>
                    <h2 className={`${classes.headerTitle} mediumLarge`}>
                        {currentStepTitle}
                    </h2>
                    <span onClick={onClose}>
                        {currentSlide === totalSlides - 1 && (
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7.67244 9.30748L0.107422 1.80245L1.10528 0.8125L8.6703 8.31752L16.2353 0.8125L17.2332 1.80245L9.66816 9.30748L17.2332 16.8125L16.2353 17.8024L8.6703 10.2974L1.10528 17.8024L0.107422 16.8125L7.67244 9.30748Z" fill="#1F1F1F"/>
                            </svg>
                        )}
                    </span>
                </div>
                <div className={classes.progressBarTop}>
                    <div
                        className={classes.progressBarInner}
                        style={{ width: `${progressPercentage}%` }}
                    ></div>
                </div>
            </header>
            <div className={classes.content} ref={contentRef}>
                <div className={`${classes.slide} ${isTransitioning ? classes.transitioning : ''}`}>
                    {slides[currentSlide]}
                </div>
            </div>
        </div>
    </div>
  );
};

export default BookingPhone;
